import React, { useState, useEffect } from 'react'
import axios from '~/data/http/axios'
import Alert from '~/Common-Objects/UI/Alert'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import TextArea from '~/Common-Objects/UI/Forms/TextArea/TextArea'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import Toggle from '~/Common-Objects/UI/Forms/Toggle/Toggle'
import helpers from '~/Classes/helpers'
import numberFormatFunctions from '~/Classes/numberFormatFunctions'
import HelpPopup from '../helpPopup/helpPopup'
import { useHasMergeTags } from '../../../campaigns/add/sms/hooks'
import AddFieldDrawer from '~/pages/cp/subscribers/customfields/incAddCustomField'

export default function DrawerSmsStep({ step, automationData, saveStep = () => {}, isThemeDesigner = false }) {
    const [campaign, setCampaign] = useState(step.stepOptions.campaign)
    const [otherCampaigns, setOtherCampaigns] = useState([])
    const [customFields, setCustomFields] = useState([])
    const [loadedFields, setLoadedFields] = useState(false)
    const [template, setTemplate] = useState(isThemeDesigner ? {} : null)
    const [options, setOptions] = useState(step.stepOptions)
    const [errors, setErrors] = useState({})
    const [saving, setSaving] = useState(false)
    const [addingField, setAddingField] = useState(false)
    const hasMergeTags = useHasMergeTags(template?.templateHTML)

    const saveCampaign = async (obj) => {
        const saveObj = {
            ...campaign,
            ...obj
        }
        setCampaign(saveObj)
        await axios.put('/campaign/' + options.campaign.id || campaign.id, { campaign: saveObj })
    }

    const saveTemplate = async () => {
        await axios.put('/template/' + step.stepOptions.campaign.TemplateId, template)
    }

    const createCampaign = async () => {
        const response = await axios.post('/campaign', {
            campaignName: 'Automation ' + automationData.id + ' Step ' + step.guid,
            type: 6,
            startDate: new Date(),
            subject: '',
            options: {},
            AutomationId: automationData.id
        })
        step.stepOptions.campaign = response.data.Campaign
        setOptions({ ...options, campaign: response.data.Campaign })
        setCampaign(response.data.Campaign)
        return response.data.Campaign
    }

    const createFromScratch = async () => {
        let newCampaign = campaign
        if (!campaign.id) {
            newCampaign = await createCampaign()
        }
        // create a blank template and bind it to the campaign
        const result = await axios.post('/template', {
            CampaignId: campaign.id,
            public: true,
            templateHTML: 'to opt out visit {{__OPTOUT}}',
            templateName: 'Sms Template',
            templateType: 6
        })
        newCampaign.TemplateId = result.data.Template.templateID
        newCampaign.subject = newCampaign.subject || ''
        newCampaign.options.smsField = newCampaign.options.smsField
        await saveCampaign(newCampaign)
        step.stepOptions.subject.value = newCampaign.subject
        step.stepOptions.customFieldId.value = newCampaign.options.smsField
        setOptions({ ...step.stepOptions })
        setTemplate(result.data.Template)
    }

    const closeStep = async () => {
        if (isThemeDesigner) {
            step.stepOptions.campaign = {
                type: 6,
                status: 'Draft',
                templateHtml: template.templateHTML
            }
            step.complete = true
            saveStep()
        } else {
            setSaving(true)
            const errors = {}
            const contentErrors = []
            if (!options.customFieldId.value || options.customFieldId.value === '-1') {
                errors.smsField = 'Required'
            }
            if (!template.templateHTML) {
                contentErrors.push('Content cannot be blank')
            }
            if (!options.subject.value) {
                errors.subject = 'Required'
            } else {
                const [subjectResponse, contentResponse] = await Promise.all([
                    axios.get('/campaign/checksms?content=' + options.subject.value),
                    axios.get('/campaign/checksms?content=' + template.templateHTML)
                ])

                if (template.templateHTML && template.templateHTML.indexOf('{{__OPTOUT}}') === -1) {
                    contentErrors.push('Content must contain {{__OPTOUT}} so people can unsubscribe from your messages')
                }

                if (subjectResponse.data.length) {
                    errors.subject = `From Name cannot contain ${subjectResponse.data.map((d) => `"${d}"`).join(' or ')}. `
                }
                if (contentResponse.data.length) {
                    contentErrors.push(`Content cannot contain ${contentResponse.data.map((d) => `"${d}"`).join(', ')}`)
                }

                if (template.templateHTML.length > 1280) {
                    contentErrors.push('Content cannot be longer than 1280 characters')
                }
            }
            if (contentErrors.length) {
                errors.content = contentErrors
            }
            setErrors(errors)
            if (Object.keys(errors).length) {
                setSaving(false)
                return
            }

            step.stepOptions.campaign = campaign
            step.complete = true
            step.wizardComplete = true
            await saveCampaign()
            await saveTemplate()
            step.stepOptions.refresh = new Date()
            saveStep()
        }
    }

    const updateCampaignField = (e, option) => {
        if (e.currentTarget.name === 'subject') {
            step.stepOptions.subject.value = e.currentTarget.value
            setOptions({ ...step.stepOptions })
        }
        if (option) {
            const options = campaign.options || {}
            setCampaign({ ...campaign, options: { ...options, [e.currentTarget.name]: e.currentTarget.value } })
        } else {
            setCampaign({ ...campaign, [e.currentTarget.name]: e.currentTarget.value })
        }
    }

    const updateTemplateField = (e) => {
        setTemplate({
            ...template,
            [e.currentTarget.name]: e.currentTarget.value
        })
    }

    const updateStepOptions = (e) => {
        let value = e.currentTarget.value
        if (e.currentTarget.name === 'skipInvalidSms') {
            value = e.currentTarget.checked
        }
        if (e.currentTarget.name === 'customFieldId') {
            value = parseInt(value)
            const options = campaign.options || {}
            setCampaign({ ...campaign, options: { ...options, smsField: value } })
        }
        if (!step.stepOptions[e.currentTarget.name]) step.stepOptions[e.currentTarget.name] = {}
        step.stepOptions[e.currentTarget.name].value = value
        setOptions({ ...step.stepOptions })
    }

    const toggleTrackLinks = () => {
        const options = campaign.options || {}
        setCampaign({ ...campaign, options: { ...options, doNotTrackLinks: !options.doNotTrackLinks } })
    }

    const finishedAddingField = (newField) => {
        axios.get('/customfield').then((res) => {
            setCustomFields(res.data.CustomFields.filter((cf) => cf.fieldType === 'TELNO'))
            updateStepOptions({ currentTarget: { name: 'customFieldId', value: newField.id } })
            setAddingField(false)
        })
    }

    useEffect(() => {
        if (!isThemeDesigner) {
            axios.get('/campaign?type=6&sortOrder=desc&hideStats=true').then((res) => {
                setOtherCampaigns(
                    Array.from(
                        new Set(
                            res.data.Campaigns.map((c) => c.subject).filter(
                                (c) => c && helpers.smsIsValidSubject(c).valid
                            )
                        )
                    )
                )
            })

            axios.get('/customfield').then((res) => {
                setCustomFields(res.data.CustomFields.filter((cf) => cf.fieldType === 'TELNO'))
                setLoadedFields(true)
            })

            if (step.stepOptions.campaign.TemplateId) {
                axios
                    .get('/template/' + step.stepOptions.campaign.TemplateId)
                    .then((res) => setTemplate(res.data.template))
            }

            if (step.stepOptions.campaign.id) {
                axios.get('/campaign/' + step.stepOptions.campaign.id).then((res) => setCampaign(res.data.Campaign))
            } else {
                createFromScratch()
            }
        } else {
            setTemplate({
                templateHTML: step.stepOptions.campaign.templateHtml || 'to opt out visit {{__OPTOUT}}'
            })
        }
    }, [])

    if ((!campaign || !template || !loadedFields) && !isThemeDesigner) return <LoadingIcons iconType='2' />

    let result
    if (template && campaign) {
        result = helpers.getSmsCredits(campaign, template)
    }
    const campaignOptions = campaign.options || {}

    if (addingField) return <AddFieldDrawer getCustomFields={finishedAddingField} standalone={true} forceType='TELNO' />

    return (
        <div>
            <div className='automationStepContentHolder'>
                <div className='mar-b10' style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <h4>SMS Field</h4>
                    <HelpPopup type={'sms'} />
                </div>
                <div className='text-grey mar-b20'>
                    Send SMS messages to your contacts, such as event reminders or notifications
                </div>

                {!customFields.length && !isThemeDesigner && (
                    <Alert
                        className='mar-b20'
                        type='warning'
                        header='You need to have a telephone number custom field to send SMS messages'
                        content={
                            <Button className='button-sml button-primary' onClick={() => setAddingField(true)}>
                                Create Telephone Field
                            </Button>
                        }
                    />
                )}

                {!!customFields.length && !isThemeDesigner && (
                    <Select
                        label='Phone Number Field'
                        explainer='This should be the contact field that contains the contact phone numbers for each contact.'
                        outerClassName='mar-b25'
                        error={errors.smsField}
                        data={customFields.map((c) => ({ label: c.fieldDesc, value: c.id }))}
                        placeholder='Select Field'
                        value={options.customFieldId.value}
                        name='customFieldId'
                        onChange={updateStepOptions}
                        filter={true}
                        updateFilter={() => {}}
                    />
                )}
                {!isThemeDesigner && (
                    <TextInput
                        outerClassName='mar-b25'
                        label='From Name'
                        explainer='Must be 3-11 characters long and have no spaces'
                        error={errors.subject}
                        value={campaign.subject}
                        name='subject'
                        onChange={updateCampaignField}
                        suggestions={otherCampaigns}
                    />
                )}

                {(Boolean(template) || isThemeDesigner) && (
                    <div>
                        <TextArea
                            id='sms_input'
                            outerClassName='mar-b25'
                            label='Message'
                            explainer='First SMS can contain up to 160 characters. Subsequent SMS have a 153 character limit.'
                            error={errors.content}
                            value={template.templateHTML}
                            name='templateHTML'
                            onChange={updateTemplateField}
                            rows='8'
                            mergeTags={true}
                            mergeTagsModal={true}
                        />
                    </div>
                )}
                {Boolean(result) && (
                    <div className='smsInfo'>
                        <div className='txtInput_label'>Content</div>
                        <div className='text-heavy'>
                            {numberFormatFunctions.formatNumber(result.html.length)} characters (max 1,280)
                        </div>
                        <div className='text-right'>
                            {result.messages > 1 ? `${result.messages} credits per send` : '1 credit per send'}
                            {hasMergeTags && '*'}
                        </div>
                    </div>
                )}
                {hasMergeTags && (
                    <div className='text-sml text-grey mar-t5'>*Merge tags could result in higher credit usage</div>
                )}

                {!isThemeDesigner && (
                    <div className='mar-b20 mar-t25'>
                        <Toggle
                            outerClassName='mar-b10'
                            after='Skip step when contacts have an invalid phone number'
                            name='skipInvalidSms'
                            checked={options.skipInvalidSms?.value}
                            onChange={updateStepOptions}
                        />
                        <Toggle
                            after='Track Links'
                            checked={!campaignOptions.doNotTrackLinks}
                            onChange={toggleTrackLinks}
                        />
                    </div>
                )}

                {!isThemeDesigner && (
                    <Alert
                        className={'mar-t10'}
                        type='info'
                        header='SMS Messages can only be sent between 9am and 8pm'
                        content='To comply with UK marketing regulations, messages cannot be sent outside this period so contacts will remain on this step until that time'
                    ></Alert>
                )}
            </div>
            <div className='automationStepSaveButtonHolder'>
                <Button className='button-primary' text='Save' disabled={saving} onClick={closeStep} />
            </div>
        </div>
    )
}
